import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import { parseScan } from "@/lib/order-scan";
import { apiErrorText } from "@/lib/scan-ui/screen";
import type { Box, CabinetSummary, PickErrorCode, PickIssue, PickResult } from "@/api/fbs";

// Хелперы экрана ФБС. Правила зеркалят бэкенд (docs/fbs-process.md §1):
// короб для ПВЗ — больше одного заказа и не тяжелее 5 кг; дедлайн задания 120 ч;
// приёмка — первые 50 штук в сутки по 15 ₽, дальше 75 ₽.

export const BOX_MAX_WEIGHT_KG = 5;
export const BOX_MIN_ORDERS = 2;
export const ACCEPTANCE_CHEAP_LIMIT = 50;

/** Состояние героя на экране сбора (S2). */
export type HeroState =
  | { kind: "idle" }
  | { kind: "resolving" }
  | { kind: "ok"; result: PickResult }
  | { kind: "warn"; result: PickResult }
  | { kind: "error"; code: PickErrorCode; text: string; cabinetName?: string };
// Код, пришедший при выключенном перехвате (летит предыдущий скан, открыта
// шторка или камера), НЕ входит сюда как отдельный `kind`: это не результат
// скана и не должно затирать героя последнего скана (см. PickPage —
// «перепечатать стикер» живёт, пока жив `hero`). Экран показывает его тостом.

/** Скан прошёл, но есть незакрытый хвост (партия/стикер/срок) → жёлтая карточка. */
export function heroForResult(result: PickResult): HeroState {
  return result.issue || result.warning ? { kind: "warn", result } : { kind: "ok", result };
}

const PLURAL_ORDERS: [string, string, string] = ["заказ", "заказа", "заказов"];
const PLURAL_PIECES: [string, string, string] = ["штука", "штуки", "штук"];
const PLURAL_BOXES: [string, string, string] = ["короб", "короба", "коробов"];

function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

export const ordersWord = (n: number): string => plural(n, PLURAL_ORDERS);
export const piecesWord = (n: number): string => plural(n, PLURAL_PIECES);
export const boxesWord = (n: number): string => plural(n, PLURAL_BOXES);

/** «через 4 ч 12 мин» / «просрочено» — остаток до дедлайна 120 ч. */
export function deadlineLeft(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - now.getTime();
  if (Number.isNaN(ms)) return "—";
  if (ms <= 0) return "просрочено";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} дн ${hours} ч`;
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  return `${minutes} мин`;
}

/** Дедлайн ближе 6 часов — подсвечиваем красным. */
export function deadlineUrgent(iso: string | null, now: Date = new Date()): boolean {
  if (!iso) return false;
  const ms = new Date(iso).getTime() - now.getTime();
  return !Number.isNaN(ms) && ms < 6 * 3600 * 1000;
}

/** Почему короб нельзя закрыть; null — можно. */
export function boxBlockReason(box: Box | null): string | null {
  if (!box) return "Короб не создан";
  if (box.orders < BOX_MIN_ORDERS) {
    return `В коробе ${box.orders} ${ordersWord(box.orders)} — в ПВЗ примут только короб, где больше одного`;
  }
  return null;
}

/** Текст веса короба. Позиции без веса в 1С показываем отдельно, а не как 0. */
export function boxWeightLabel(box: Box): string {
  const base = `${box.weight_kg.toFixed(2)} / ${BOX_MAX_WEIGHT_KG} кг`;
  if (box.without_weight > 0) {
    return `${base} · вес неизвестен по ${box.without_weight}`;
  }
  return base;
}


/**
 * Порядок строк в списке к сборке (S2) и в потребности добора (S3).
 *
 * Правила заказчика, и они не совпадают с «просто отсортировать»:
 *  * закрытые позиции уезжают ВНИЗ — они больше не работа;
 *  * только что отсканированная — НАВЕРХ: кладовщик держит товар в руках и
 *    должен видеть, сколько его ещё осталось, не листая список;
 *  * закрытая позиция наверх НЕ всплывает — правило «закрытое вниз» сильнее,
 *    иначе последний скан подвешивал бы наверху уже собранный артикул.
 *
 * Сортировка стабильная (тайбрейк по исходному индексу): внутри группы
 * сохраняется порядок сервера, а он осмысленный — сначала то, что по учёту
 * лежит в комнате.
 */
export function sortScanRows<T>(
  rows: T[],
  opts: { key: (row: T) => string; done: (row: T) => boolean; last: string | null },
): T[] {
  const rank = (row: T): number => {
    if (opts.done(row)) return 2;
    return opts.last !== null && opts.key(row) === opts.last ? 0 : 1;
  };
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
    .map((entry) => entry.row);
}

/** Человеческий статус поставки. Незнакомое значение показываем как есть —
 *  подменять его на «открыта» значило бы врать про состояние. */
export function supplyStatusLabel(status: string | null): string {
  if (!status) return "не создана";
  const known: Record<string, string> = {
    open: "открыта",
    delivered: "в доставке",
    closed: "закрыта",
  };
  return known[status] ?? status;
}


/** Подпись про приёмку: сколько дешёвых штук осталось сегодня. */
export function acceptanceLabel(cabinet: CabinetSummary): string {
  if (cabinet.cheap_left > 0) {
    return `${cabinet.accepted_today} из ${ACCEPTANCE_CHEAP_LIMIT} по 15 ₽`;
  }
  return `лимит 15 ₽ исчерпан — дальше 75 ₽/шт`;
}

/** Группы шторки «Разбор» — в том же порядке, что чинит бэкенд (fixups.py). */
export const ISSUE_GROUPS: { key: PickIssue; title: string; hint: string }[] = [
  {
    key: "no_sticker",
    title: "Стикер не напечатан",
    hint: "Штуку без стикера в ПВЗ не примут — печатаем и клеим",
  },
  {
    key: "no_partia",
    title: "Партия не определена",
    hint: "Без партии позиция не попадёт в документы 1С",
  },
  {
    key: "no_sgtin",
    title: "КИЗ не отправлен в WB",
    hint: "Без КИЗ при невыкупе товар вернётся платным автовозвратом",
  },
  {
    key: "sgtin_rejected",
    title: "КИЗ не прошёл проверку WB",
    hint: "С таким КИЗ WB не пропустит поставку в доставку",
  },
  {
    key: "no_expiration",
    title: "Срок годности не принят WB",
    hint: "WB требует не меньше 30 дней от текущей даты",
  },
];

/** Статус «проверка ещё идёт» — блокирует доставку, но это не ошибка КИЗ. */
export const SGTIN_PENDING = "pending";

/** «WB ещё не закрепил КИЗ» (первые 10 минут после отправки) — тоже ждём, не
 *  ошибка. Та же граница, что `sgtin_status.SGTIN_WAITING` на бэкенде. */
export const SGTIN_NOT_ATTACHED_YET = "sgtinNotAttachedYet";

/** «WB ещё не закрепил новый КИЗ» (первые 10 минут после отправки в задании
 *  лежит прежний код — так бывает после замены и повторной отправки) — ждём. */
export const SGTIN_MISMATCH_YET = "sgtinMismatchYet";

/** «На проверке у WB»: КИЗ отправлен меньше 2 минут назад, а WB ещё не отдал
 *  по заданию блок `sgtin` вовсе — ждём (`sgtin_status.SGTIN_STATUS_NOT_YET`). */
export const SGTIN_STATUS_NOT_YET = "sgtinStatusNotYet";

/** Как часто экран сбора зовёт фоновую проверку КИЗ (сервер к WB — не чаще
 *  раза в 15 с на поставку, сколько бы телефонов ни опрашивали). */
export const SGTIN_POLL_MS = 20000;

/** Опрашивать ли дальше: пока есть отправленные КИЗ без итога — ждём итога;
 *  пока висят забракованные — сообщение должно обновиться после действий
 *  (в том числе с другого телефона). Последний опрос упал — повторяем (ошибка
 *  при этом на экране), иначе единичный сбой WB выключил бы проверку до
 *  следующего скана. Ждать нечего — опрос выключен до следующего скана
 *  (экран перечитывает опрос после каждого). */
export function sgtinPollInterval(
  data: Pick<fbs.SgtinPoll, "unresolved" | "rejected" | "errors"> | undefined,
  failed: boolean,
): number | false {
  if (failed) return SGTIN_POLL_MS;
  if (!data) return false;
  const waiting = data.unresolved > 0 || data.rejected.length > 0 || data.errors.length > 0;
  return waiting ? SGTIN_POLL_MS : false;
}

/** Подписывать ли строки сообщения юрлицом и поставкой: опрос идёт по всем
 *  открытым поставкам склада, и без подписи штука ЧУЖОГО юрлица читалась бы
 *  как своя. Не нужно, только когда все строки — из поставки этого экрана. */
export function sgtinShowSupply(
  rows: Pick<fbs.SgtinRejected, "supply_id">[],
  currentSupplyId: string | null,
): boolean {
  const supplies = new Set(rows.map((r) => r.supply_id));
  if (supplies.size > 1) return true;
  return supplies.size === 1 && !supplies.has(currentSupplyId ?? "");
}

/** Подпись поставки строки сообщения: «Омега · WB-GI-282927858». */
export function sgtinSupplyLabel(
  row: Pick<fbs.SgtinRejected, "cabinet_name" | "wb_supply_id">,
): string {
  return [row.cabinet_name, row.wb_supply_id].filter(Boolean).join(" · ");
}

/** Как штуку искать глазами: наклейка стикера (partA partB); стикер не
 *  напечатан — остаётся номер задания. */
export function sgtinStickerLabel(
  row: Pick<fbs.SgtinRejected, "sticker_part_a" | "sticker_part_b" | "wb_order_id">,
): string {
  const parts = [row.sticker_part_a, row.sticker_part_b].filter(Boolean).join(" ");
  return parts ? `наклейка ${parts}` : `задание ${row.wb_order_id} (стикера нет)`;
}

/** Можно ли «Отправить без кода» у штуки из перечня блокирующих: только когда WB КИЗ
 *  ЗАБРАКОВАЛ. «Проверка идёт» и «ещё не закрепил» ждут, а не чинят удалением.
 *  Та же граница, что `sgtin_status.is_rejected` на бэкенде (он же и откажет 409). */
export function sgtinRemovable(row: Pick<fbs.SgtinRow, "blocking" | "decision">): boolean {
  return (
    row.blocking &&
    row.decision != null &&
    row.decision !== SGTIN_PENDING &&
    row.decision !== SGTIN_NOT_ATTACHED_YET &&
    row.decision !== SGTIN_MISMATCH_YET &&
    row.decision !== SGTIN_STATUS_NOT_YET
  );
}

/** Что будет, если отправить без кода, — текст подтверждения «Отправить без кода». */
export const SGTIN_REMOVE_WARNING =
  "Штука уйдёт покупателю без КИЗ (код маркировки уберём из задания WB), и " +
  "поставку можно будет передать в доставку. При невыкупе или возврате WB " +
  "вернёт её к нам платным автовозвратом. Повторно КИЗ в это задание не отправится.";

/** Штука, чей КИЗ меняют («Заменить товар»): как её найти в коробе и что за товар. */
export interface SgtinReplaceTarget {
  pickId: string;
  wbOrderId: number;
  stickerPartA: string | null;
  stickerPartB: string | null;
  boxPosition: number | null;
  product: string;
}

/** Цель замены — из строки красного сообщения экрана сбора или строки «Разбора». */
export function sgtinReplaceTarget(
  row: Pick<
    fbs.SgtinRejected,
    | "pick_id"
    | "wb_order_id"
    | "sticker_part_a"
    | "sticker_part_b"
    | "box_position"
    | "article"
    | "nomenclature_name"
  >,
): SgtinReplaceTarget {
  return {
    pickId: row.pick_id,
    wbOrderId: row.wb_order_id,
    stickerPartA: row.sticker_part_a,
    stickerPartB: row.sticker_part_b,
    boxPosition: row.box_position,
    product: [row.article, row.nomenclature_name].filter(Boolean).join(" · "),
  };
}

/** Что сделать руками: какую штуку вынуть, откуда, и что сканировать взамен. */
export function sgtinReplacePrompt(t: SgtinReplaceTarget): string {
  const sticker = sgtinStickerLabel({
    sticker_part_a: t.stickerPartA,
    sticker_part_b: t.stickerPartB,
    wb_order_id: t.wbOrderId,
  });
  const where = t.boxPosition != null ? ` из короба №${t.boxPosition}` : "";
  return (
    `Выньте штуку (${sticker})${where}, отсканируйте код маркировки другого ` +
    `экземпляра: ${t.product || `товар задания ${t.wbOrderId}`}. Наклейку ` +
    `переклейте на новую штуку и положите её в тот же короб.`
  );
}

/** Скан в диалоге замены → тело запроса или причина отказа (запрос не шлём).
 *  Разбор — тот же `parseScan`, что у основного скана; код уходит как есть
 *  (GS не восстанавливаем — сервер без GS откажет сам, с объяснением). */
export function sgtinReplaceScan(
  raw: string,
): { body: fbs.SgtinReplaceRequest } | { error: string } {
  const parsed = parseScan(raw);
  if (parsed.kind === "datamatrix" && parsed.gtin) {
    return { body: { cim_code: parsed.raw, gtin: parsed.gtin, serial: parsed.serial ?? "" } };
  }
  if (parsed.kind === "ean13") {
    return {
      error:
        "Это штрихкод EAN13, а нужен код маркировки (Честный знак) другого экземпляра",
    };
  }
  return { error: "Код не распознан: отсканируйте код маркировки (Честный знак)" };
}

/** «Повторить всё» — проход «Разбора» пачками, ОДИН на оба экрана.
 *
 * ⚠️ Цикл живёт здесь, а не в экранах: копий было две (шторка скана и карточка
 * завершения), и они уже разошлись — после сорока проходов одна писала
 * «осталось N», вторая «Разбор пуст». Правило остановки тут не «список
 * опустел» (часть причин не чинится в принципе: партии нет в 1С), а «за проход
 * не починилось ничего», иначе цикл был бы вечным ровно на тех позициях, ради
 * которых «Разбор» и существует. */
export function useRetryAllIssues(
  supplyId: string | null,
  cabinetId: number,
  apply: (issues: fbs.Issues) => void,
  onProgress: (text: string) => void,
) {
  return useMutation({
    mutationFn: async () => {
      let fixed = 0;
      let error = "";
      let left = 0;
      // ⚠️ Смещение сдвигаем, когда за проход НЕ ПОЧИНИЛОСЬ НИЧЕГО: иначе
      // пачка вечно упиралась бы в одни и те же неисправимые строки — а они
      // идут ПЕРВЫМИ (список отсортирован по причине), и тридцать позиций без
      // стикера закрывали бы собой сотню чинящихся партий. Починилось —
      // начинаем сначала: список сдвинулся сам.
      let offset = 0;
      for (let pass = 0; pass < RETRY_ALL_PASSES; pass++) {
        const r = await fbs.retryAllIssues(supplyId!, cabinetId, offset);
        fixed += r.fixed;
        error = error || r.first_error;
        left = r.total;
        apply(r);
        onProgress(`починено ${fixed}, осталось ${left}`);
        if (r.total === 0) break;
        if (r.fixed > 0) offset = 0;
        else {
          offset += r.tried;
          if (r.tried === 0 || offset >= r.total) break;
        }
      }
      return { fixed, left, error };
    },
    onSuccess: ({ fixed, left, error }) => {
      onProgress("");
      if (left === 0) toast.success(`Разбор пуст: починено ${fixed}`);
      else
        toast.warning(`Починено ${fixed}, осталось ${left}` + (error ? `. ${error}` : ""), {
          duration: 12000,
        });
    },
    onError: (e) => {
      onProgress("");
      toast.error(apiErrorText(e, "Повторить всё не удалось"));
    },
  });
}

/** Потолок проходов: защита от вечного цикла, а не рабочее ограничение —
 *  штатно проход кончается, когда чинить перестало получаться. */
const RETRY_ALL_PASSES = 40;

/** Короткая подпись причины «Разбора» — ОДНА на портал.
 *
 * ⚠️ Подписей было три: `ISSUE_GROUPS` (заголовки шторки), этот словарь (его
 * никто не читал) и локальная копия в карточке завершения — где один и тот же
 * `no_sticker` читался «нет стикера» вместо «Стикер не напечатан». Кладовщик
 * видит одну и ту же строку одной и той же поставки в двух местах. */
export const ISSUE_TITLE: Record<PickIssue, string> = {
  no_sticker: "Стикер не напечатан",
  no_partia: "Партия не определена",
  no_expiration: "Срок годности не принят WB",
  no_sgtin: "КИЗ не отправлен в WB",
  sgtin_rejected: "КИЗ не прошёл проверку WB",
};

/** Заголовок ошибки скана для героя. */
export const PICK_ERROR_TITLE: Record<PickErrorCode, string> = {
  duplicate: "Уже сканировали",
  unknown_product: "Товар не найден в 1С",
  // Штрихкод в 1С заведён у нескольких товаров — текст называет оба;
  // чинится в справочнике 1С, повтор скана не поможет.
  barcode_ambiguous: "Штрихкод у нескольких товаров в 1С",
  wrong_cabinet: "Другое юрлицо",
  no_task: "Заданий больше нет",
  // Обычно этот код перехватывается модалкой «принять без партии» и до героя
  // не доходит; заголовок нужен на случай, если ответ придёт вне ветки EAN13.
  partia_not_found: "Партии нет в 1С",
  // Справочник привязок 1С недоступен — повтор скана через минуту поможет.
  mp_binding_unavailable: "1С недоступна",
  // Не ошибка кладовщика и не сбой: покупатель отменил заказ, пока шла сборка.
  // Задание бэк уже погасил, штуку надо вернуть на полку.
  order_cancelled: "Заказ отменён покупателем",
  error: "Ошибка",
};

/**
 * Память «последняя партия по артикулу» на экране сбора (S2).
 *
 * Со склада берут подряд из одной партии, а в штрихкоде EAN13 её нет — без
 * памяти кладовщик перенабирал бы один и тот же номер на каждую штуку.
 *
 * Ключ памяти — АРТИКУЛ, а не штрихкод: у номенклатуры может быть несколько
 * EAN13 (старые/новые этикетки), и номер партии у них общий. Связку
 * «штрихкод → артикул» узнаём из ответа сервера на успешный скан — на самом
 * скане артикул ещё неизвестен. Живёт только в пределах экрана: партия — вещь
 * сегодняшняя, тянуть её в следующую смену нельзя.
 */
export interface PartiaMemory {
  articleByEan: Record<string, string>;
  partiaByArticle: Record<string, string>;
}

export function createPartiaMemory(): PartiaMemory {
  return { articleByEan: {}, partiaByArticle: {} };
}

/** Что подставить в поле партии для этого штрихкода. Пусто — ещё не знаем. */
export function suggestPartia(mem: PartiaMemory, ean13: string): string {
  const article = mem.articleByEan[ean13];
  if (!article) return "";
  return mem.partiaByArticle[article] ?? "";
}

/** Запомнить партию принятой штуки (и заодно связку «штрихкод → артикул»). */
export function rememberPartia(
  mem: PartiaMemory,
  ean13: string,
  article: string,
  partia: string,
): void {
  if (!ean13 || !article) return;
  mem.articleByEan[ean13] = article;
  if (partia) mem.partiaByArticle[article] = partia;
}

/**
 * Сообщение о заданиях, собранных в кабинете WB мимо портала (статус
 * `external`). Пустой список — `null`: говорить нечего. Молча из списка к
 * сбору задания не пропадают — человек видит, сколько и какие.
 */
export function externalNotice(orders: number[]): string | null {
  if (!orders.length) return null;
  const n = orders.length;
  return (
    `${n} ${plural(n, ["заказ собран", "заказа собраны", "заказов собраны"])} ` +
    `в кабинете WB — ${plural(n, ["убран", "убраны", "убраны"])} из списка к сбору: ` +
    orders.join(", ")
  );
}

/**
 * «Обновить заказы» — один хук на все экраны ФБС.
 *
 * Кнопка есть и на главном, и в сборе; когда мутация была скопирована в оба
 * места, любая правка текста или ключа кэша делалась в одном из них, и
 * поведение разъезжалось.
 */
export function useSyncOrders(onAfter?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fbs.syncOrders(),
    onSuccess: (data) => {
      qc.setQueryData(["fbs-overview"], data.overview);
      const failed = data.cabinets.filter((c) => c.error);
      const added = data.cabinets.reduce((sum, c) => sum + c.created, 0);
      if (failed.length) {
        toast.error(failed.map((c) => `${c.cabinet_name}: ${c.error}`).join("; "));
      } else {
        toast.success(added ? `Новых заданий: ${added}` : "Новых заданий нет");
      }
      const cancelled = data.cabinets.flatMap((c) => c.cancelled);
      if (cancelled.length) {
        toast.warning(
          `Покупатель отменил заказов: ${cancelled.length} — убраны из списка к сбору: ` +
            cancelled.join(", "),
          { duration: 15000 },
        );
      }
      const external = externalNotice(data.cabinets.flatMap((c) => c.external));
      if (external) toast.warning(external, { duration: 15000 });
      onAfter?.();
    },
    onError: (e) => toast.error(apiErrorText(e, "Синхронизация не удалась")),
  });
}
